import { type CustomCellEditorProps } from "ag-grid-react";
import { useCallback, useEffect } from "react";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import type { CustomFieldDefinition } from "commonComponents/entity/customField/utils/customFieldDefinition.util";
import {
    getOptionsFromDefinition,
    parseCommaSeparatedIds,
} from "commonComponents/entity/customField/utils/customFieldTypes/selectCustomFieldUtils";

interface IUserSubCellEditorParams {
    definition?: CustomFieldDefinition;
    multiple?: boolean;
}

export const UserSubCellEditor = (
    props: CustomCellEditorProps<unknown, string> & IUserSubCellEditorParams,
) => {
    const { value, onValueChange, stopEditing, definition, multiple = false } = props;

    const treeData = getOptionsFromDefinition(definition);

    const selectedValues = multiple
        ? parseCommaSeparatedIds(value ?? "")
        : (() => {
              const id = value ? parseInt(value, 10) : null;
              return id != null && !isNaN(id) ? [id] : [];
          })();

    useEffect(() => {
        if (!multiple) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" || event.key === "Tab") {
                stopEditing();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [multiple, stopEditing]);

    const getPopupContainer = useCallback((triggerNode: HTMLElement): HTMLElement => {
        return triggerNode.parentNode instanceof HTMLElement
            ? triggerNode.parentNode
            : document.body;
    }, []);

    const columnWidth = props.column?.getActualWidth();
    const widthStyle = columnWidth
        ? { width: `${Math.max(0, columnWidth - 8)}px`, marginLeft: 4, marginTop: 4 }
        : undefined;

    return (
        <div className="custom-field-select-editor" style={widthStyle}>
            <BTSelect
                id="userSubCellEditor"
                data-testid="userSubCellEditor"
                value={selectedValues}
                treeData={treeData}
                defaultOpen
                // eslint-disable-next-line jsx-a11y/no-autofocus -- editor opens from a deliberate cell double-click; focus must land in the search input or the editor is unusable
                autoFocus
                dropdownCanExpand={false}
                getPopupContainer={getPopupContainer}
                onChange={(_id, val) => {
                    if (multiple) {
                        const selected = (val as number[]) ?? [];
                        onValueChange(selected.length > 0 ? selected.join(",") : null);
                    } else {
                        onValueChange(val != null ? String(val as number) : null);
                        stopEditing();
                    }
                }}
                onBlur={multiple ? () => {} : () => stopEditing()}
                multiple={multiple}
            />
        </div>
    );
};
